import { useState } from "react";
import { ArrowLeft, Trash2, AlertTriangle, Mail, Shield } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"confirm" | "verify" | "success">("confirm");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleRequestDeletion = async () => {
    setLoading(true);
    setError("");

    try {
      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setError("You must be logged in to delete your account");
        setLoading(false);
        return;
      }

      setEmail(user.email || "");

      // Send OTP to user's email
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: user.email!,
        options: {
          shouldCreateUser: false,
        },
      });

      if (otpError) throw otpError;

      setStep("verify");
    } catch (err: any) {
      setError(err.message || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndDelete = async () => {
    setLoading(true);
    setError("");

    try {
      // Verify OTP
      const {
        data: { user },
        error: verifyError,
      } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "email",
      });

      if (verifyError) throw verifyError;
      if (!user) throw new Error("Verification failed");

      // Call Edge Function to delete account
      const { error: deleteError } = await supabase.functions.invoke(
        "delete-account",
        {
          body: { userId: user.id },
        },
      );

      if (deleteError) throw deleteError;

      // Sign out
      await supabase.auth.signOut();

      setStep("success");
    } catch (err: any) {
      setError(err.message || "Failed to verify code or delete account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Delete Account
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Permanently remove your account and data
          </p>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 space-y-6">
          {/* Step 1: Confirmation */}
          {step === "confirm" && (
            <>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <h3 className="font-semibold text-red-900 dark:text-red-100">
                      Warning: This action cannot be undone
                    </h3>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      Deleting your account will permanently remove:
                    </p>
                    <ul className="text-sm text-red-800 dark:text-red-200 space-y-1 ml-4 list-disc">
                      <li>Your profile and account information</li>
                      <li>All learning progress and achievements</li>
                      <li>Practice session history</li>
                      <li>Tournament participation records</li>
                      <li>Earned XP and badges</li>
                      <li>Parent portal connections</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Before you go...
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  If you're having issues with the app, please contact our
                  support team at{" "}
                  <a
                    href="mailto:support@ithopu.com"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    support@ithopu.com
                  </a>
                  . We're here to help!
                </p>
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {error}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleRequestDeletion}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {loading ? "Sending..." : "Continue to Delete"}
                </Button>
              </div>
            </>
          )}

          {/* Step 2: OTP Verification */}
          {step === "verify" && (
            <>
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-2">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Verify Your Email
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We've sent a verification code to:
                </p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {email}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enter 6-digit code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="000000"
                    className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    maxLength={6}
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {error}
                    </p>
                  </div>
                )}

                <div className="text-center">
                  <button
                    onClick={handleRequestDeletion}
                    disabled={loading}
                    className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    Didn't receive the code? Resend
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep("confirm")}
                  className="flex-1"
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  onClick={handleVerifyAndDelete}
                  disabled={loading || otp.length !== 6}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {loading ? "Deleting..." : "Delete Account"}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Success */}
          {step === "success" && (
            <>
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-2">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Account Deleted
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Your account and all associated data have been permanently
                  deleted.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  We're sorry to see you go. If you change your mind, you can
                  always create a new account.
                </p>
              </div>

              <Button onClick={() => navigate("/")} className="w-full">
                Return to Home
              </Button>
            </>
          )}
        </div>

        {/* Footer */}
        {step !== "success" && (
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              Return to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
